// Single source of truth for masterclass landing-page content (copy lives in
// i18n/locales/<code>/masterclass.json; this file maps ids → message keys).

// Funnel A/B variant map — each entry is a distinct VSL video.
// URL pattern: /masterclass/{slug}. The slug is also persisted on the user record
// as `masterclassFunnelVariant` so we can attribute conversions per variant.
// `null` videoId means "use the default landing experience" (the /masterclass route).
export type FunnelVariant = {
  slug: string;
  youtubeId: string;
  label: string;
  // When true, the variant is excluded from the random /masterclass/start
  // rotation (e.g. a low performer) but still resolvable by its direct
  // /masterclass/<slug> URL so existing links + attribution keep working.
  excludeFromRotation?: boolean;
  // Vidalytics embed id (VSL-optimized player). When set, the hero renders the
  // Vidalytics player instead of the YouTube one; the poster/thumbnail is then
  // managed in the Vidalytics dashboard, not from the YouTube id.
  vidalyticsId?: string;
};

// Shared Vidalytics player/account id — the `9BMXzyV1` segment in every embed
// loader URL (https://fast.vidalytics.com/embeds/9BMXzyV1/<embedId>/). Doubles
// as the Vidalytics conversion-tracking id (first arg of ConversionTracking.track).
export const VIDALYTICS_PLAYER_ID = '9BMXzyV1';

// Vidalytics conversion ids (Dashboard → Conversions). Firing these client-side
// attributes the lead/sale back to the VSL the viewer watched, so Vidalytics'
// own analytics + split-testing can rank variants on conversions and revenue.
export const VIDALYTICS_CONVERSIONS = {
  lead: 'oJKdb4Ax',
  sale: 'hBacbDST',
} as const;

export const MASTERCLASS_VARIANTS: FunnelVariant[] = [
  { slug: 'v1-actor', youtubeId: 'ZbyyxJTwVBs', label: 'Hook 1 — Actor', vidalyticsId: 'Uup9U9vLtJMSkpax' },
  { slug: 'v1-ai', youtubeId: 'dhf_elchKdc', label: 'Hook 1 — AI', vidalyticsId: 'YMe7Myx79L_Wrmvk', excludeFromRotation: true },
  { slug: 'v2-actor', youtubeId: 'S-IU_zg4QDc', label: 'Hook 2 — Actor', vidalyticsId: 'QFbKLmJkDDtpJTJV' },
  { slug: 'v2-ai', youtubeId: 'kOAHd-B-OA8', label: 'Hook 2 — AI', excludeFromRotation: true },
  { slug: 'v3-actor', youtubeId: 'BzigHtBPOCY', label: 'Hook 3 — Actor', excludeFromRotation: true },
  { slug: 'v3-ai', youtubeId: 'hBk_JtIhalE', label: 'Hook 3 — AI', excludeFromRotation: true },
  { slug: 'v4-actor', youtubeId: 'TdngBo3nwzA', label: 'Hook 4 — Actor', excludeFromRotation: true },
  { slug: 'v4-ai', youtubeId: 'nRpDUkek7HE', label: 'Hook 4 — AI', vidalyticsId: '__4fG1Wt_dF9BzOY', excludeFromRotation: true },
  { slug: 'v5-actor', youtubeId: 'K9ubWSjtxTE', label: 'Hook 5 — Actor', excludeFromRotation: true },
  { slug: 'v5-ai', youtubeId: 'feBb37yuSHs', label: 'Hook 5 — AI', excludeFromRotation: true },
];

// The pool the /masterclass/start random rotation draws from. Low performers are
// kept in the catalogue above (still resolvable by direct /masterclass/<slug>
// URL) but dropped from rotation.
//
// Rotation now = v1-actor, v2-actor (owner, 2026-09-30: two-video A/B, v2-actor on Vidalytics).
// Previously v1-actor, v1-ai, v4-ai (trimmed 2026-07-07).
// 2026-07-07 read (GA4 click→lead + owned impression→signup + owned sales, two
// independent datasets agreeing): v1-actor is the clear winner — top lead rate
// (~25.6%/24.1%) AND the most sales. v1-ai/v4-ai are the strongest challengers
// (~24%). Cut this round: v2-actor (bottom lead rate of the served-5, 21.6% in
// both datasets) + v4-actor (middling leads AND 0 sales from 163 signups).
// Long-excluded (weakest / never tracked): v2-ai, v3-actor, v3-ai, v5-actor
// (18% leads, laggard), v5-ai.
export const MASTERCLASS_ROTATION_VARIANTS: FunnelVariant[] = MASTERCLASS_VARIANTS.filter(
  (v) => !v.excludeFromRotation,
);

export const findVariantBySlug = (slug: string | undefined): FunnelVariant | null => {
  if (!slug) return null;
  return MASTERCLASS_VARIANTS.find((v) => v.slug === slug) ?? null;
};

// Display copy below is stored as i18n keys (namespace `masterclass`) — resolve
// with `t()` at render. Ids / values / slugs stay stable (analytics + stored
// quiz answers depend on them).
export type TrustLogo = { name: string };
export type BenefitCard = { icon: string; titleKey: string; subtitleKey: string };
export type PlatformFeature = { icon: string; titleKey: string; descKey: string };
export type HowItWorksStep = { titleKey: string; descriptionKey: string };
export type Qualifier = string;
export type Testimonial = {
  id: number;
  flag: string;
  duration: string;
  image: string;
  // YouTube Shorts video — embedded via iframe in the lightbox.
  youtubeShortsId: string | null;
  earningsKey: string;
  periodKey: string;
  name: string;
  locationKey: string;
  wasKey: string;
  quoteKey: string;
};
// Geo-localized amounts interpolated into an FAQ answer message. Each entry is
// formatted against the visitor's geo currency at render:
//   { aed: 50000 }                  → useGeoCurrency().formatFromAed(50000)
//   { aedMin: 15000, aedMax: 200000 } → useGeoCurrency().formatRangeFromAed(...)
export type FaqAmount = { aed: number } | { aedMin: number; aedMax: number };
export type Faq = {
  questionKey: string;
  answerKey: string;
  // Bold lead-in rendered before the answer (e.g. "No.").
  leadKey?: string;
  amounts?: Record<string, FaqAmount>;
  category: string;
};
export type FaqCategory = { id: string; labelKey: string };

export type QuizOption = {
  value: string;
  emoji?: string;
  labelKey?: string;
  subKey?: string;
  flag?: string;
};

// Mirrors the developer set on the main landing (components/agents/Partners.vue).
// Rendered as a horizontally-scrolling marquee in the masterclass hero.
export const TRUST_LOGOS: TrustLogo[] = [
  { name: 'EMAAR' },
  { name: 'DAMAC' },
  { name: 'SOBHA' },
  { name: 'NAKHEEL' },
  { name: 'MERAAS' },
  { name: 'ARADA' },
  { name: 'AZIZI' },
  { name: 'BINGHATTI' },
  { name: 'ELLINGTON' },
  { name: 'OMNIYAT' },
  { name: 'DANUBE' },
  { name: 'DEYAAR' },
  { name: 'SELECT GROUP' },
  { name: 'MAG' },
  { name: 'DUBAI PROPERTIES' },
  { name: 'TIGER' },
  { name: 'SAMANA' },
  { name: 'OBJECT 1' },
  { name: 'IMKAN' },
  { name: 'ALDAR' },
];

export const BENEFIT_CARDS: BenefitCard[] = [
  { icon: '⚡', titleKey: 'masterclass.benefits.fast.title', subtitleKey: 'masterclass.benefits.fast.subtitle' },
  {
    icon: '💰',
    titleKey: 'masterclass.benefits.commission.title',
    subtitleKey: 'masterclass.benefits.commission.subtitle',
  },
  {
    icon: '🎓',
    titleKey: 'masterclass.benefits.noExperience.title',
    subtitleKey: 'masterclass.benefits.noExperience.subtitle',
  },
];

const feature = (icon: string, id: string): PlatformFeature => ({
  icon,
  titleKey: `masterclass.features.${id}.title`,
  descKey: `masterclass.features.${id}.desc`,
});

export const DESKTOP_FEATURES: PlatformFeature[] = [
  feature('🗺️', 'map'),
  feature('🏗️', 'inventory'),
  feature('⭐', 'exclusive'),
  feature('🎓', 'training'),
];

export const MOBILE_FEATURES: PlatformFeature[] = [
  feature('📱', 'phone'),
  feature('📊', 'analytics'),
  feature('💳', 'paymentPlans'),
  feature('🔔', 'alerts'),
];

export const HOW_IT_WORKS: HowItWorksStep[] = ['apply', 'trained', 'sell'].map((id) => ({
  titleKey: `masterclass.howItWorksSteps.${id}.title`,
  descriptionKey: `masterclass.howItWorksSteps.${id}.description`,
}));

export const QUALIFIER_YES: Qualifier[] = [
  'masterclass.qualifiers.yes.ambitious',
  'masterclass.qualifiers.yes.salesperson',
  'masterclass.qualifiers.yes.alreadySell',
  'masterclass.qualifiers.yes.investors',
  'masterclass.qualifiers.yes.freedom',
  'masterclass.qualifiers.yes.coachable',
];

export const QUALIFIER_NO: Qualifier[] = [
  'masterclass.qualifiers.no.getRichQuick',
  'masterclass.qualifiers.no.noWork',
  'masterclass.qualifiers.no.passive',
  'masterclass.qualifiers.no.noSystem',
];

const testimonialKeys = (id: string) => ({
  earningsKey: `masterclass.testimonialItems.${id}.earnings`,
  periodKey: `masterclass.testimonialItems.${id}.period`,
  locationKey: `masterclass.testimonialItems.${id}.location`,
  wasKey: `masterclass.testimonialItems.${id}.was`,
  quoteKey: `masterclass.testimonialItems.${id}.quote`,
});

export const TESTIMONIALS: Testimonial[] = [
  {
    id: 1,
    flag: '🇬🇧',
    duration: '1:26',
    image: '/images/masterclass/testimonials/michael-thumb.jpg',
    youtubeShortsId: 'SnHwU4W7Kpo',
    name: 'Michael T.',
    ...testimonialKeys('michael'),
  },
  {
    id: 2,
    flag: '🇮🇳',
    duration: '0:18',
    image: '/images/masterclass/testimonials/priya-thumb.jpg',
    youtubeShortsId: 'FphvgRsjdJg',
    name: 'Priya S.',
    ...testimonialKeys('priya'),
  },
  {
    id: 3,
    flag: '🇷🇺',
    duration: '0:24',
    image: '/images/masterclass/testimonials/alexei-thumb.jpg',
    youtubeShortsId: 'CGLQKo9OzDE',
    name: 'Alexei V.',
    ...testimonialKeys('alexei'),
  },
  {
    id: 4,
    flag: '🇪🇺',
    duration: '0:29',
    image: '/images/masterclass/testimonials/maria-thumb.jpg',
    youtubeShortsId: 'QpgdZ_Z54ME',
    name: 'Maria L.',
    ...testimonialKeys('maria'),
  },
];

export const FAQ_CATEGORIES: FaqCategory[] = [
  { id: 'all', labelKey: 'masterclass.faqCategories.all' },
  { id: 'getting-started', labelKey: 'masterclass.faqCategories.gettingStarted' },
  { id: 'money', labelKey: 'masterclass.faqCategories.money' },
  { id: 'platform', labelKey: 'masterclass.faqCategories.platform' },
  { id: 'legal', labelKey: 'masterclass.faqCategories.legal' },
];

const faq = (id: string, category: string, extra: Pick<Faq, 'leadKey' | 'amounts'> = {}): Faq => ({
  questionKey: `masterclass.faqs.${id}.question`,
  answerKey: `masterclass.faqs.${id}.answer`,
  category,
  ...extra,
});

export const FAQS: Faq[] = [
  faq('experience', 'getting-started'),
  faq('cost', 'getting-started'),
  faq('partTime', 'getting-started'),
  faq('location', 'getting-started', { leadKey: 'masterclass.faqs.location.lead' }),
  faq('earnings', 'money', {
    amounts: {
      entry: { aedMin: 15000, aedMax: 30000 },
      active: { aedMin: 50000, aedMax: 100000 },
      top: { aedMin: 100000, aedMax: 300000 },
      example: { aed: 100000 },
      earn: { aed: 60000 },
    },
  }),
  faq('sevenDay', 'money'),
  faq('tiers', 'money'),
  faq('payout', 'money'),
  faq('noDeals', 'money', { leadKey: 'masterclass.faqs.noDeals.lead' }),
  faq('platform', 'platform'),
  faq('support', 'platform'),
  faq('licence', 'legal', { leadKey: 'masterclass.faqs.licence.lead' }),
  faq('legitimate', 'legal'),
  faq('mlm', 'legal', { leadKey: 'masterclass.faqs.mlm.lead' }),
  faq('taxes', 'legal'),
];

// ───────────────────────────── Quiz options ─────────────────────────────

export const QUIZ_IDENTITY_OPTS: QuizOption[] = [
  {
    value: 'yes-active',
    emoji: '🏆',
    labelKey: 'masterclass.quizIdentity.yesActive.label',
    subKey: 'masterclass.quizIdentity.yesActive.sub',
  },
  {
    value: 'yes-past',
    emoji: '🔄',
    labelKey: 'masterclass.quizIdentity.yesPast.label',
    subKey: 'masterclass.quizIdentity.yesPast.sub',
  },
  {
    value: 'no-sales',
    emoji: '💼',
    labelKey: 'masterclass.quizIdentity.noSales.label',
    subKey: 'masterclass.quizIdentity.noSales.sub',
  },
  {
    value: 'no-none',
    emoji: '🚀',
    labelKey: 'masterclass.quizIdentity.noNone.label',
    subKey: 'masterclass.quizIdentity.noNone.sub',
  },
];

export const QUIZ_PROBLEM_OPTS: QuizOption[] = [
  { value: 'low-income', emoji: '📉', labelKey: 'masterclass.quizProblem.lowIncome' },
  { value: 'bad-splits', emoji: '✂️', labelKey: 'masterclass.quizProblem.badSplits' },
  { value: 'slow-pay', emoji: '⏳', labelKey: 'masterclass.quizProblem.slowPay' },
  { value: 'no-opportunity', emoji: '🚪', labelKey: 'masterclass.quizProblem.noOpportunity' },
];

export const QUIZ_AGITATION_OPTS: QuizOption[] = [
  {
    value: '40+',
    emoji: '🔥',
    labelKey: 'masterclass.quizAgitation.fullTime.label',
    subKey: 'masterclass.quizAgitation.fullTime.sub',
  },
  {
    value: '20-40',
    emoji: '💪',
    labelKey: 'masterclass.quizAgitation.verySerious.label',
    subKey: 'masterclass.quizAgitation.verySerious.sub',
  },
  {
    value: '10-20',
    emoji: '⚡',
    labelKey: 'masterclass.quizAgitation.serious.label',
    subKey: 'masterclass.quizAgitation.serious.sub',
  },
  {
    value: '5-10',
    emoji: '🌱',
    labelKey: 'masterclass.quizAgitation.testing.label',
    subKey: 'masterclass.quizAgitation.testing.sub',
  },
];

// AED amounts exposed numerically so the quiz modal can re-format them
// against the visitor's geo currency; the "/ month" tail comes from the
// `masterclass.common.perMonth` message.
export type QuizSolutionOption = QuizOption & {
  aedMin: number;
  aedMax: number | null;
};

export const QUIZ_SOLUTION_OPTS: QuizSolutionOption[] = [
  { value: '10k', aedMin: 10_000, aedMax: 30_000, subKey: 'masterclass.quizSolution.supplemental' },
  { value: '30k', aedMin: 30_000, aedMax: 50_000, subKey: 'masterclass.quizSolution.replace' },
  { value: '50k', aedMin: 50_000, aedMax: 100_000, subKey: 'masterclass.quizSolution.wealth' },
  { value: '100k', aedMin: 100_000, aedMax: null, subKey: 'masterclass.quizSolution.topEarner' },
];

export const QUIZ_GEO_OPTS: QuizOption[] = [
  { value: 'uae', flag: '🇦🇪', labelKey: 'masterclass.quizGeo.uae' },
  { value: 'uk', flag: '🇬🇧', labelKey: 'masterclass.quizGeo.uk' },
  { value: 'europe', flag: '🇪🇺', labelKey: 'masterclass.quizGeo.europe' },
  { value: 'india', flag: '🇮🇳', labelKey: 'masterclass.quizGeo.india' },
  { value: 'africa', flag: '🌍', labelKey: 'masterclass.quizGeo.africa' },
  { value: 'americas', flag: '🌎', labelKey: 'masterclass.quizGeo.americas' },
  { value: 'russia', flag: '🇷🇺', labelKey: 'masterclass.quizGeo.russia' },
  { value: 'other', flag: '🌏', labelKey: 'masterclass.quizGeo.other' },
];

export const VISION_ITEMS = [
  'masterclass.quizVision.notification',
  'masterclass.quizVision.bank',
  'masterclass.quizVision.laptop',
  'masterclass.quizVision.team',
];

export const CALCULATING_STEPS = [
  'masterclass.quizCalculating.availability',
  'masterclass.quizCalculating.experience',
  'masterclass.quizCalculating.earning',
  'masterclass.quizCalculating.matching',
  'masterclass.quizCalculating.reviewing',
  'masterclass.quizCalculating.finalizing',
];
